import PublicHomeView from '@views/PublicHomeView.vue'
import { configureRouter } from "@/.shared/services/routing-services";
import HomeView from '@/views/AuthorizedHomeView.vue';
import OperacionesView from '@/views/OperacionesView.vue';
import { useAuthentication, ExtendedUserInfo } from '@/.shared/services/auth';
import { isAccesoRestringidoPorEmpresaAgentesNoValidada } from '@/composables/empresaAgentesPortalRestringido';
import AgentesView from '@/views/AgentesView.vue';
import AgentesInmobiliariaView from '@/views/AgentesInmobiliariaView.vue';
import MisDatosView from "@/views/MisDatosView.vue";
import ClientesView from '@/views/ClientesView.vue';
import LiquidacionesView from '@/views/LiquidacionesView.vue';
import DatosFacturaView from '@/views/DatosFacturaView.vue';
import SoporteView from '@/views/SoporteView.vue';
import TicketView from '@/views/TicketView.vue';
import TramitesView from '@/views/TramitesView.vue';
import ComparacionesView from '@/views/ComparacionesView.vue';
import ComunicacionesOperacionView from '@/views/ComunicacionesOperacionView.vue';
import InmueblesView from '@/views/InmueblesView.vue';
import { usePermissionCheckers } from '@/.shared/services/auth/authorization/permission-checkers';
import { PermissionsApp, useAuthorization } from '@/.shared/services/auth/authorization/authorization-service-impl';
import NotAuthorizedView from '@/.shared/views/NotAuthorizedView.vue';

export function configureAppRoutes() {
  configureRouter(router => {

    router.addRoute("home", b =>
      b
        .withPath("")
        .withComponent(PublicHomeView)
        .allowAnonymous()
        .configure(r => {
          r.beforeEnter = async (to, from, next) => {

            const auth = useAuthentication();
            if (auth.isLoggedIn()) {
              const u = auth.userInfo as ExtendedUserInfo;
              if (isAccesoRestringidoPorEmpresaAgentesNoValidada(u)) {
                return next({ name: "mis-datos" });
              }
              const autho = useAuthorization();
              if (autho.hasPermission(PermissionsApp.ViewNuevaVenta)) {
                return next({ name: "operaciones" });
              }
              if (autho.hasPermission(PermissionsApp.ViewNuevaVentaInmobiliaria)) {
                return next({ name: "inmuebles" });
              }
            }

            next();
          }
          r.props = route => ({
            preferredIdp: route.query.preferredIdp as string || null
          });
        }));


    router.addRoute("private-home", d => {
      d.withPath("home").withComponent(HomeView).withAuthorization({});
    });

    router.addRoute("nueva-venta", d => {
      d.withPath("nueva-venta/:id?")
        .withComponent(() => import('@/views/NuevaVentaView.vue'))
        .withAuthorization({})
        .configure(r => {
          r.props = route => ({
            id: route.params.id || null,
            idComparacion: route.query.idComparacion || null,
            idInmueble: route.query.idInmueble || null
          });
          r.beforeEnter = (to, from, next) => {
            const autho = useAuthorization();
            if (autho.hasPermission(PermissionsApp.ViewNuevaVenta)) {
              next();
            } else {
              next(NotAuthorizedView);
            }
          };
        });
    });

    router.addRoute("buscador-ofertas", d => {
      d.withPath("buscador-ofertas")
        .withComponent(() => import('@/views/BuscadorOfertasEnergiaView.vue'))
        .withAuthorization({ authReqs: [PermissionsApp.SearchOfertasEnergia] });
    });

    router.addRoute("comparaciones", d => {
      d.withPath("comparaciones").withComponent(ComparacionesView).withAuthorization({});
    });

    router.addRoute("operaciones", d => {
      d.withPath("operaciones").withComponent(OperacionesView).withAuthorization({ authReqs: usePermissionCheckers().canAccessOperacionesEnergia });
    });

    router.addRoute("tramites", d => {
      d.withPath("tramites").withComponent(TramitesView).withAuthorization({});
    });

    router.addRoute("agentes", d => {
      d.withPath("agentes").withComponent(AgentesView).withAuthorization({});
    });

    router.addRoute("agentes-inmobiliaria", d => {
      d.withPath("agentes-inmobiliaria").withComponent(AgentesInmobiliariaView).withAuthorization({});
    });

    router.addRoute("clientes", d => {
      d.withPath("clientes").withComponent(ClientesView).withAuthorization({});
    });

    router.addRoute("mis-datos", d => {
      d.withPath("mis-datos").withComponent(MisDatosView).withAuthorization({});
    });

    router.addRoute("liquidaciones", d => {
      d.withPath("liquidaciones").withComponent(LiquidacionesView).withAuthorization({});
    });

    router.addRoute("datos-factura", d => {
      d.withPath("datos-factura").withComponent(DatosFacturaView).withAuthorization({})
        .configure(r => {
          r.props = route => ({ esAutofactura: route.query.esAutofactura === "true" });
        });
    });

    router.addRoute("soporte", d => {
      d.withPath("soporte").withComponent(SoporteView).withAuthorization({});
    });

    router.addRoute("comunicados", d => {
      d.withPath("comunicados").withComponent(() => import('@/views/ComunicadosView.vue')).withAuthorization({});
    });

    router.addRoute("ticket", d => {
      d.withPath("ticket/:id?").withComponent(TicketView).withAuthorization({})
        .configure(r => {
          r.props = true
        })
    });

    router.addRoute("inmuebles", d => {
      d.withPath("inmuebles").withComponent(InmueblesView).withAuthorization({});
    });

    router.addRoute("comunicaciones-operacion", d => {
      d.withPath("comunicaciones-operacion/:idOperacion/:codigoSecuencialOperacion/:tipo").withComponent(ComunicacionesOperacionView).withAuthorization({ authReqs: usePermissionCheckers().canAccessOperacionesEnergia })
        .configure(r => {
          r.props = route => {
            const tipo = route.params.tipo as string;
            const tipoNormalizado = (tipo === "LUZ" || tipo === "GAS") ? "ENERGIA" : tipo;
            return {
              idOperacion: route.params.idOperacion,
              codigoSecuencialOperacion: route.params.codigoSecuencialOperacion,
              tipo: tipoNormalizado
            };
          };
        });
    });

    router.addRoute("nueva-venta-inmobiliaria", d => {
      d.withPath("nueva-venta-inmobiliaria")
        .withComponent(() => import('@/views/NuevaVentaInmobiliariaView.vue'))
        .withAuthorization({})
        .configure(r => {
          r.props = route => ({
            idInmueble: route.query.idInmueble || null,
            idClienteInmobiliario: route.query.idClienteInmobiliario || null
          });
          r.beforeEnter = (to, from, next) => {
            const autho = useAuthorization();
            if (autho.hasPermission(PermissionsApp.ViewNuevaVentaInmobiliaria)) {
              next();
            } else {
              next(NotAuthorizedView);
            }
          };
        });
    });

    router.addRoute("nuevo-borrador-operacion", d => {
      d.withPath("nuevo-borrador-operacion")
        .withComponent(() => import('@/views/NuevoBorradorOperacionView.vue'))
        .withAuthorization({})
        .configure(r => {
          r.props = route => ({
            idInmueble: route.query.idInmueble || null,
            idClienteInmobiliario: route.query.idClienteInmobiliario || null
          });
        });
    });

    router.addRoute("autoregistro-empresa-agentes", d => {
      d.withPath("autoregistro-empresa")
        .withComponent(() => import('@/views/AutoregistroEmpresaAgentesView.vue'))
        .allowAnonymous()
        .configure(r => {
          r.beforeEnter = (to, from, next) => {
            const auth = useAuthentication();
            if (auth.isLoggedIn()) {
              return next({ name: "home" });
            }
            next();
          };
        });
    })

  })
}